import React from "react";
import Table from "@cloudscape-design/components/table";
import Pagination from "@cloudscape-design/components/pagination";
import Header from "@cloudscape-design/components/header";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Alert from "@cloudscape-design/components/alert";
import SpaceBetween from "@cloudscape-design/components/space-between";
import CollectionPreferences from "@cloudscape-design/components/collection-preferences";
import Link from "@cloudscape-design/components/link";
import StatusIndicator from "@cloudscape-design/components/status-indicator";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { tgBotsStore } from "../stores/tgBots";
import { configStore } from "../stores/config";
import { tgBotsAddModalStore } from "../stores/tgBotsAddModal";
import { tgBotsEditModalStore } from "../stores/tgBotsEditModal";
import { tgBotsRotationStore } from "../stores/tgBotsRotation";
import { userStore } from "../stores/access";

class TgBots extends React.Component {
	componentDidMount() {
		this.props.Interface.loadTgBotsList();
	}

	formatDateTime = (value) => {
		if (!value) return "-";

		const date = new Date(value);
		if (Number.isNaN(date.getTime())) return value;

		return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
	};

	getStatusType = (value) => {
		if (["disabled", "inactive", "off", "banned", "ban", "error", "failed"].includes(value)) return "error";
		if (["free"].includes(value)) return "info";
		if (["current"].includes(value)) return "success";

		return "in-progress";
	};

	canEditToken = () => (this.props.user.level >= 6  || this.props.user.showColumn.includes("showTgToken"));

	openEditModal = (item) => {
		const statusOptions = this.props.tgBotsEditModal.currentOption.status || [];
		const currentStatus = statusOptions.find((option) => option.value === item.status) || { value: item.status || "free", label: item.status || "free" };

		this.props.setTgBotsEditModal({
			...this.props.tgBotsEditModal,
			show: true,
			loaded: true,
			id: item.id,
			bot_link: item.bot_link || "",
			status: currentStatus,
			priority: String(item.priority ?? ""),
			token: this.canEditToken() ? item.token || "" : "",
		});
	};

	render() {
		const { tgBots } = this.props;

		return (
			<>
				{tgBots.loaded && tgBots.error.text.length ? (
					<Box margin={{ bottom: "m" }}>
						<Alert type="error" header="Error loading data:">
							<Box variant="p">{tgBots.error.text}</Box>
							<hr />
							<Box variant="p">
								request error code: {tgBots.error.code} | request timestamp: {tgBots.error.timestamp}
							</Box>
						</Alert>
					</Box>
				) : null}
				<Table
					stickyHeader
					trackBy="id"
					loading={!tgBots.loaded}
					sortingColumn={{ sortingField: tgBots.sorting.sortingColumn.column }}
					sortingDescending={tgBots.sorting.isDescending}
					onSortingChange={(sorting) => {
						this.props.setTgBots({
							...tgBots,
							sorting: {
								...tgBots.sorting,
								sortingColumn: {
									order: !sorting.detail.isDescending ? "ASC" : "DESC",
									column: sorting.detail.sortingColumn.sortingField,
								},
								isDescending: sorting.detail.isDescending,
							},
						});
						setTimeout(() => this.props.Interface.loadTgBotsList(), 250);
					}}
					columnDefinitions={[
						{
							id: "id",
							header: "ID",
							sortingField: "id",
							cell: (item) => (
								<Button iconName="status-in-progress" variant="normal" onClick={() => this.openEditModal(item)}>
									{item.id}
								</Button>
							),
						},
						{
							id: "bot_link",
							header: "Bot link",
							sortingField: "bot_link",
							cell: (item) => <Link>{item.bot_link || "-"}</Link>,
						},
						{
							id: "status",
							header: "Status",
							sortingField: "status",
							cell: (item) => item.status ? <StatusIndicator type={this.getStatusType(item.status)}>{item.status}</StatusIndicator> : "-",
						},
						{
							id: "priority",
							header: "Priority",
							sortingField: "priority",
							cell: (item) => item.priority ?? "-",
						},
						{
							id: "token",
							header: "Token",
							sortingField: "token",
							cell: (item) => item.token || "-",
						},
						{
							id: "date",
							header: "Date",
							sortingField: "date",
							cell: (item) => this.formatDateTime(item.date),
						},
					]}
					items={tgBots.data}
					pagination={
						<Pagination
							disabled={!tgBots.loaded}
							currentPageIndex={tgBots.pagination.current}
							pagesCount={tgBots.pagination.total}
							onChange={(page) => {
								this.props.setTgBots({
									...tgBots,
									pagination: { ...tgBots.pagination, current: page.detail.currentPageIndex },
								});
								setTimeout(() => this.props.Interface.loadTgBotsList(), 250);
							}}
						/>
					}
					preferences={
						<CollectionPreferences
							title="Preferences"
							confirmLabel="Confirm"
							cancelLabel="Cancel"
							preferences={{ pageSize: this.props.config.tgBots.perPage }}
							pageSizePreference={{
								title: "Telegram bots per page",
								options: this.props.config.tgBots.preferences.perPage,
							}}
							onConfirm={(preferences) => {
								localStorage.setItem("tgBots.perPage", preferences.detail.pageSize);
								this.props.setConfig({
									...this.props.config,
									tgBots: { ...this.props.config.tgBots, perPage: preferences.detail.pageSize },
								});
								setTimeout(() => this.props.Interface.loadTgBotsList(), 250);
							}}
						/>
					}
					header={
						<Header
							description="Below listed all Telegram bots"
							actions={
								<SpaceBetween direction="horizontal" size="xs">
									<Button
										disabled={!tgBots.loaded}
										onClick={() => this.props.setTgBotsRotation({ ...this.props.tgBotsRotation, show: true })}
									>
										Auto rotation
									</Button>
									<Button
										variant="primary"
										iconName="add-plus"
										disabled={!tgBots.loaded}
										onClick={() => this.props.setTgBotsAddModal({ ...this.props.tgBotsAddModal, show: true })}
									>
										Add Telegram bot
									</Button>
								</SpaceBetween>
							}
						>
							Telegram bots ({tgBots.pagination.count})
						</Header>
					}
					empty="No Telegram bots found"
				/>
			</>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const tgBots = useRecoilValue(tgBotsStore);
		const setTgBots = useSetRecoilState(tgBotsStore);
		const config = useRecoilValue(configStore);
		const setConfig = useSetRecoilState(configStore);
		const tgBotsAddModal = useRecoilValue(tgBotsAddModalStore);
		const setTgBotsAddModal = useSetRecoilState(tgBotsAddModalStore);
		const tgBotsEditModal = useRecoilValue(tgBotsEditModalStore);
		const setTgBotsEditModal = useSetRecoilState(tgBotsEditModalStore);
		const tgBotsRotation = useRecoilValue(tgBotsRotationStore);
		const setTgBotsRotation = useSetRecoilState(tgBotsRotationStore);
		const user = useRecoilValue(userStore);

		return (
			<Component
				{...props}
				tgBots={tgBots}
				setTgBots={setTgBots}
				config={config}
				setConfig={setConfig}
				tgBotsAddModal={tgBotsAddModal}
				setTgBotsAddModal={setTgBotsAddModal}
				tgBotsEditModal={tgBotsEditModal}
				setTgBotsEditModal={setTgBotsEditModal}
				tgBotsRotation={tgBotsRotation}
				setTgBotsRotation={setTgBotsRotation}
				user={user}
			/>
		);
	};
}

export default withRecoil(TgBots);
